import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { DATE_FORMATS, ROUTE_STATUS, formatDate, getDistanceInMiles, getPageLimit, getSecondsDurationInHoursMins, showError, showSuccess, updatePageLimit } from 'utils';
import { tableStyles } from '../../../css/tableStyles';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import { deleteRoute, fetchRoutes, markRouteAsDelivered, removeRouteFromPickPackList } from 'services';
import RowActionComponent from 'components/RowActionComponent';
import AddRouteToPickPackListModal from 'components/AddRouteToPickPackListModal';
import PickPackInfoToolTip from './PickPackInfoToolTip';
import Filter from './Filter';
import qs from 'query-string';
import ExportCSVButton from 'components/ExportCSVButton';
import Currency from 'components/Currency/Currency';
import CustomLink from 'components/TableLink';
import { Loading } from 'components/loading/Loading';

const columnsForCSV = [
  {
    label: 'Route ID',
    value: 'objectId',
  },
  {
    label: 'Route Name',
    value: 'name',
  },
  {
    label: 'Distance (m)',
    value: 'estimatedRoutingDistance',
  },
  {
    label: 'Duration (min)',
    value: 'estimatedRoutingDuration',
  },
  {
    label: 'No of Orders',
    value: 'noOfOrders',
  },
  {
    label: 'Delivery Date',
    value: 'deliveryDateTime',
  },
];

let filters: {
  [p: string]: any;
  page: number;
} = {
  page: 0,
};

const customStyles = {
  ...tableStyles,
  table: {
    style: {
      paddingBottom: '65px',
      backgroundColor: '#FAFAFA',
    },
  },
};

const Routes = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [routes, setRoutes] = useState<Record<string, any>>({});
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [addToPickPackListModalOpen, setAddtoPickPackListModalOpen] = useState<boolean>(false);
  const [renderTooltip, setRenderTooltip] = useState<boolean>(false);
  const [tooltipData, setTooltipData] = useState<Record<string, any>>({});

  const fetchRoutesFn = async () => {
    setLoading(true);
    const { page, ...rest } = filters;
    try {
      const response = await fetchRoutes({
        page,
        limit: getPageLimit(),
        filter: {
          ...rest,
        },
      });
      setRoutes(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const queryStringToFilter = () => {
    return qs.parse(props.location.search, { parseBooleans: true, parseNumbers: true });
  };

  useEffect(() => {
    filters = {
      ...filters,
      ...queryStringToFilter(),
    };
    fetchRoutesFn();
  }, []);

  const markRouteAsDeliveredFn = async (id: string) => {
    try {
      await markRouteAsDelivered(id);
      fetchRoutesFn();
    } catch (e) {
      showError(e);
    }
  };

  const removeRouteFromPickPackListFn = async (id: string) => {
    try {
      await removeRouteFromPickPackList(id);
      showSuccess('Route removed from Pick Pack List successfully');
      fetchRoutesFn();
    } catch (e) {
      showError(e);
    }
  };

  const deleteRouteFn = async (id: string) => {
    try {
      await deleteRoute(id);
      fetchRoutesFn();
    } catch (e) {
      showError(e);
    }
  };

  const setTooltipDataFn = (data: Record<string, any>) => {
    setTooltipData(data.pickPackList);
    setRenderTooltip(true);
  };

  const getActions = (row: Record<string, any>) => {
    const actions: Record<string, any>[] = [];
    const addAction = (id: string) => {
      setSelectedRouteIds([id]);
      setAddtoPickPackListModalOpen(true);
    };
    if (row.status !== ROUTE_STATUS.DELIVERED) {
      actions.push({
        text: 'Mark Route As Delivered',
        onClick: () => markRouteAsDeliveredFn(row.objectId),
      });
    }
    if (row.pickPackList) {
      actions.push({
        text: 'Remove from Pick Pack List',
        onClick: () => removeRouteFromPickPackListFn(row.objectId),
      });
    } else {
      actions.push({
        text: 'Add to Pick Pack List',
        onClick: () => addAction(row.objectId),
      });
    }
    actions.push(
      {
        text: 'Delete',
        onClick: () => deleteRouteFn(row.objectId),
        customClass: styles['delete'],
      },
    );
    return actions;
  };

  const columns = useMemo(() => [
    {
      name: 'Route ID',
      selector: 'objectId',
      sortable: false,
      width: '120px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/routes/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: 'name',
      sortable: true,
    },
    {
      name: 'Created At',
      selector: 'createdAt',
      sortable: true,
      format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
    },
    {
      name: 'Delivery Date',
      selector: 'deliveryDateTime',
      sortable: true,
      format: (row) => formatDate(row.deliveryDateTime, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
    },
    {
      name: 'Status',
      selector: 'status',
      sortable: true,
    },
    {
      name: 'No. of Orders',
      selector: 'noOfOrders',
      sortable: true,
      format: row => row.noOfOrders?.toString() || '-',
    },
    {
      name: 'Distance',
      selector: 'originalEstimatedRoutingDistance',
      sortable: true,
      cell: row => getDistanceInMiles(row.originalEstimatedRoutingDistance),
    },
    {
      name: 'Duration',
      selector: 'originalEstimatedRoutingDuration',
      sortable: true,
      cell: row => getSecondsDurationInHoursMins(row.originalEstimatedRoutingDuration),
    },
    {
      name: 'Pick Pack List ID',
      selector: 'route',
      sortable: false,
      width: '175px',
      // eslint-disable-next-line react/display-name
      cell: (row) => (
        <>
          <div data-for={'pick-pack-list-detail'} data-tip={''}>
            <div
              onClick={() => setTooltipDataFn(row)}
            >
              {row.pickPackList?.objectId || '-'}
            </div>
          </div>
          {renderTooltip && (
            <PickPackInfoToolTip
              pickPackInfo={tooltipData || null}
              hideToolTip={() => setRenderTooltip(false)}
            />
          )}
        </>
      ),
    },
    {
      name: 'Earning',
      selector: 'routeEarning',
      // react-display-name
      // eslint-disable-next-line react/display-name
      cell: row => (
        <Currency amount={row.routeEarning} />
      ),
    },
    {
      name: '',
      width: '80px',
      // eslint-disable-next-line react/display-name
      cell: (row) => (
        <RowActionComponent
          actions={getActions(row)}
        />
      ),
    },
  ], [renderTooltip]);

  const setSelectedRoutes = (data: Record<string, any>) => {
    const { selectedRows } = data;
    setSelectedRouteIds(selectedRows.map(row => row.objectId));
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    fetchRoutesFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const filterToQueryString = () => {
    return qs.stringify({ ...filters });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onFilterSubmit = (updatedFilter: Record<string, any>) => {
    filters = { ...updatedFilter, page: 0 };
    updateQueryString();
    fetchRoutesFn();
  };

  if (loading && props.data) return <Loading type={'mini'} />;

  return (
    <div className={`${styles['main-container']}`}>
      {addToPickPackListModalOpen && (
        <AddRouteToPickPackListModal
          routeIds={selectedRouteIds}
          onSuccess={() => {
            setSelectedRouteIds([]);
            fetchRoutesFn();
          }}
          toggleModal={() => {
            setSelectedRouteIds([]);
            setAddtoPickPackListModalOpen(!addToPickPackListModalOpen);
          }}
        />
      )}
      {!props.data && (
        <div className={styles['header']}>
          <div className={styles['heading']}>Routes</div>
          <div className={styles['buttons-container']}>
            <ExportCSVButton
              data={routes.items}
              fileName={'Routes'}
              columnsForCSV={columnsForCSV}
            />
            {selectedRouteIds.length > 0 && (
              <ActionButton
                text={'Add to Pick Pack List'}
                active={true}
                isLoading={false}
                onClick={() => setAddtoPickPackListModalOpen(true)}
                customClass={styles['add-list-button']}
              />
            )}
            <ActionButton
              text={<>Route <span>+</span></>}
              active={true}
              isLoading={false}
              onClick={() => props.history.push('/routes/create')}
              customClass={styles['add-new-route-button']}
            />
          </div>
        </div>
      )}
      <Filter {...props} onSubmit={onFilterSubmit} />
      <div className={styles['data-table']}>
        <DataTable
          keyField="objectId"
          columns={columns}
          noHeader={true}
          data={props.data || routes.items}
          highlightOnHover={true}
          customStyles={customStyles}
          pagination={!props.data}
          paginationServer={true}
          paginationPerPage={getPageLimit()}
          paginationDefaultPage={(filters.page || 0) + 1}
          paginationTotalRows={routes.total}
          paginationRowsPerPageOptions={[50, 100, 200]}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          selectableRows={true}
          onSelectedRowsChange={props.setSelectedRoutes || setSelectedRoutes}
          selectableRowSelected={row => selectedRouteIds.includes(row.objectId)}
          loading={loading}
        />
      </div>
    </div>
  );
};

export { Routes };
